import { placeParamsToURL } from 'helpers/CommonHelper';
import { hasUserAccessFunctionality } from 'helpers/PermissionHelper';
import { t } from 'i18next';
import React, { useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { NavLink, useLocation } from 'react-router-dom';
import { badgeCountsSelector } from 'store/admin/selectors/AdminGlobalSelector';
import { useAppSelector } from 'store/hooks';

import type { MenuGroup, MenuItem } from './sidebarConfig';

import './../../assets/scss/adminSidebar.scss';

interface SidebarProps {
  menu: MenuGroup[];
  maxWidth?: number | string;
  minWidth?: number;
  Header: React.FC;
  Footer: React.FC;
  collasped: boolean;
  openMobileMenu: boolean;
  setOpenMobileMenu?: (open: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  menu,
  maxWidth = 240,
  minWidth = 64,
  Header,
  Footer,
  collasped,
  openMobileMenu,
  setOpenMobileMenu,
}) => {
  const location = useLocation();
  const [openKey, setOpenKey] = useState<string | null>(null);

  const badgeCounts = useAppSelector(badgeCountsSelector);

  const toggleMenu = (key: string) => {
    setOpenKey((prev) => (prev === key ? null : key));
  };

  const pathMatches = (locationPath: string, configPath: string) => {
    if (locationPath === configPath) return true;
    if (configPath.includes(':')) {
      const regex = new RegExp(
        '^' + configPath.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/:[^/]+/g, '[^/]+') + '$'
      );
      return regex.test(locationPath);
    }
    return locationPath.startsWith(configPath + '/');
  };

  const isActiveRoute = (item: MenuItem) => {
    if (!item.path) return false;
    if (location.pathname === item.path) return true;
    if (item.activePaths?.some((path) => pathMatches(location.pathname, path))) return true;
    return false;
  };

  const getActiveParent = (): string | null => {
    for (const group of menu) {
      for (const item of group.items) {
        if (!item.children) continue;
        const hasActiveChild = item.children.some((child) =>
          pathMatches(location.pathname, child.path)
        );
        if (hasActiveChild) return item.key;
      }
    }
    return null;
  };

  const getMenuItemPath = (menuItem: MenuItem) => {
    if (menuItem.tabRedirects) {
      const redirects = Array.isArray(menuItem.tabRedirects)
        ? menuItem.tabRedirects
        : Object.values(menuItem.tabRedirects);

      const firstAllowed = redirects.find((redirect: any) =>
        hasUserAccessFunctionality(redirect.permission || redirect.permissions)
      );
      if (firstAllowed) {
        return placeParamsToURL(firstAllowed.path);
      }
    }
    return menuItem.path ?? '';
  };

  const getMenuItemLabel = (menuItem: MenuItem) => {
    if (menuItem.tabRedirects) {
      const redirects = Array.isArray(menuItem.tabRedirects)
        ? menuItem.tabRedirects
        : Object.values(menuItem.tabRedirects);

      const allowedRedirects = redirects.filter((redirect: any) =>
        hasUserAccessFunctionality(redirect.permission || redirect.permissions)
      );
      if (allowedRedirects.length === 1) {
        const rawLabel = allowedRedirects[0].label;
        return rawLabel ? t(rawLabel) : menuItem.label;
      }
    }
    return menuItem.label;
  };

  const activeParent = getActiveParent();

  return (
    <div
      className={`sidebar text-light d-flex flex-column dark-mode left-sidebar ${
        collasped ? 'sidebar-open' : 'sidebar-close'
      } ${openMobileMenu ? 'open-mobile-menu' : ''}`}
      style={{ width: collasped ? maxWidth : minWidth }}
    >
      <Header />

      <ul className="sidebar-menu list-unstyled flex-grow-1">
        {menu.map((group) => {
          const visibleItems = group.items.filter((item) =>
            hasUserAccessFunctionality(item?.permission)
          );
          if (!visibleItems.length) return null;

          return (
            <React.Fragment key={group.title}>
              <li className={`sidebar-title my-2 ${collasped ? 'opacity-100' : 'opacity-0'}`}>
                {group.title}
              </li>

              {visibleItems.map((item) => {
                const isRouteActive = activeParent === item.key || isActiveRoute(item);
                const isOpen = isRouteActive || openKey === item.key;

                return (
                  <li key={item.key}>
                    {item.path && (
                      <NavLink
                        to={getMenuItemPath(item)}
                        className={`sidebar-link ${isActiveRoute(item) ? 'active' : ''}`}
                        end
                        onClick={() => {
                          setOpenKey(null);
                          if (setOpenMobileMenu) setOpenMobileMenu(false);
                        }}
                      >
                        <span className="active-icon d-flex align-items-center gap-2">
                          <span className="menu-icon">{item.svgIcon}</span>
                          {collasped && getMenuItemLabel(item)}
                        </span>

                        {item.showBadge && item.badgeKey && badgeCounts[item.badgeKey] > 0 && (
                          <span className="menu-item-badge">{badgeCounts[item.badgeKey]}</span>
                        )}
                      </NavLink>
                    )}

                    {item.children && (
                      <>
                        <div
                          className={`sidebar-link sidebar-parent ${isRouteActive ? 'active' : ''}`}
                          onClick={() => toggleMenu(item.key)}
                        >
                          <span className="active-icon d-flex align-items-center gap-2">
                            <span className="menu-icon">{item.svgIcon}</span>
                            {collasped && getMenuItemLabel(item)}
                          </span>
                          {collasped && (
                            <span className={`sidebar-arrow ${isOpen ? 'open' : ''}`}>▾</span>
                          )}
                        </div>

                        <Collapse in={isOpen}>
                          <ul className="sidebar-submenu list-unstyled">
                            {item.children.map((child) => (
                              <li key={child.key}>
                                <NavLink
                                  to={child.path}
                                  className={({ isActive }) =>
                                    `sidebar-sublink ${isActive ? 'active' : ''}`
                                  }
                                  onClick={() => {
                                    if (setOpenMobileMenu) setOpenMobileMenu(false);
                                  }}
                                >
                                  {child.label}
                                </NavLink>
                              </li>
                            ))}
                          </ul>
                        </Collapse>
                      </>
                    )}
                  </li>
                );
              })}
            </React.Fragment>
          );
        })}
      </ul>

      <Footer />
    </div>
  );
};

export default Sidebar;
